import { useState, useEffect } from 'react';

interface UseAppLoaderOptions {
  minLoadingTime?: number; // Minimum time to show loader (ms)
  checkAuthenticationState?: boolean; // Whether to check authentication
  skipLoaderOnSubsequentVisits?: boolean; // Skip loader for returning users in same session
}

export const useAppLoader = (options: UseAppLoaderOptions = {}) => {
  const {
    minLoadingTime = 2500, // 2.5 seconds minimum loading time
    checkAuthenticationState = true,
    skipLoaderOnSubsequentVisits = true
  } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    const initializeApp = async () => {
      const startTime = Date.now();
      
      // Check if this is a subsequent visit in the same session
      if (skipLoaderOnSubsequentVisits) {
        const hasLoadedBefore = sessionStorage.getItem('app_has_loaded');
        if (hasLoadedBefore) {
          setIsLoading(false);
          setIsFirstLoad(false);
          return;
        }
      }

      // Simulate app initialization tasks
      const tasks = [
        { name: 'Initializing application...', weight: 20 },
        { name: 'Loading configuration...', weight: 15 },
        { name: 'Setting up routing...', weight: 15 },
        { name: 'Checking authentication...', weight: 25 },
        { name: 'Loading user preferences...', weight: 15 },
        { name: 'Finalizing...', weight: 10 }
      ];

      let currentProgress = 0;

      // Execute tasks with progress updates
      for (const task of tasks) {
        await new Promise(resolve => setTimeout(resolve, Math.random() * 300 + 100));
        currentProgress += task.weight;
        setLoadingProgress(Math.min(currentProgress, 100));
      }

      // Additional checks if needed
      if (checkAuthenticationState) {
        // Check for existing authentication
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        
        // Simulate auth validation time
        if (token && user) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // Ensure minimum loading time is met
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minLoadingTime) {
        await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsedTime));
      }

      // Mark as loaded in session storage
      if (skipLoaderOnSubsequentVisits) {
        sessionStorage.setItem('app_has_loaded', 'true');
      }

      setLoadingProgress(100);
      
      // Small delay before hiding loader for better UX
      setTimeout(() => {
        setIsLoading(false);
        setIsFirstLoad(false);
      }, 200);
    };

    initializeApp();
  }, [minLoadingTime, checkAuthenticationState, skipLoaderOnSubsequentVisits]);

  // Reset on page refresh (not on route changes)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (skipLoaderOnSubsequentVisits) {
        // Clear session storage on page refresh/close
        sessionStorage.removeItem('app_has_loaded');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [skipLoaderOnSubsequentVisits]);

  return {
    isLoading,
    isFirstLoad,
    loadingProgress,
    setIsLoading // Allow manual control if needed
  };
};
